import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple } from "@phosphor-icons/react";
import {
  LOGO_URL,
  CONTACT,
  FCA_DISCLOSURE,
  FEE_DISCLOSURE,
  RISK_WARNING,
  COMPANY_DETAILS,
  UK_REGIME_NOTE,
} from "../data/content";

const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-ink text-neutral-300">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
      <div className="grid gap-14 md:grid-cols-12">
        <div className="md:col-span-3">
          <div className="inline-flex rounded-2xl bg-white p-3">
            <img src={LOGO_URL} alt="BTK Mortgage Services" className="h-12 w-auto" />
          </div>
          <p className="mt-6 text-sm leading-relaxed text-neutral-400 max-w-xs">
            Boutique mortgage &amp; insurance advisory. Personal service, whole-of-market access,
            and advice in plain English — from first call to keys.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {CONTACT.socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-neutral-300 transition-[background-color,color,border-color] duration-200 hover:bg-brand hover:border-brand hover:text-white"
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white">Explore</h3>
          <ul className="mt-5 space-y-3 text-sm">
            {[
              ["Home", "/"],
              ["First-Time Buyer", "/mortgages/first-time-buyer"],
              ["Next-Time Buyer", "/mortgages/next-time-buyer"],
              ["Remortgage", "/mortgages/remortgage"],
              ["Buy-to-Let", "/mortgages/buy-to-let"],
              ["Protection", "/mortgages/protection"],
              ["Whole of Life", "/mortgages/whole-of-life"],
              ["Business Protection", "/mortgages/business-protection"],
              ["Calculators", "/calculators"],
              ["How It Works", "/how-it-works"],
              ["Jargon Buster", "/resources/jargon-buster"],
              ["Guides", "/resources/guides"],
              ["Refer a Friend", "/resources/refer-a-friend"],
              ["About", "/about"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link
                  to={to}
                  data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-neutral-400 transition-colors duration-200 hover:text-white"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white">Talk to us</h3>
          <ul className="mt-5 space-y-4 text-sm">
            <li>
              <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="flex items-start gap-3 text-neutral-300 transition-colors duration-200 hover:text-white">
                <Phone size={18} weight="duotone" className="mt-0.5 text-brand-coral" />
                {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="flex items-start gap-3 text-neutral-300 transition-colors duration-200 hover:text-white">
                <EnvelopeSimple size={18} weight="duotone" className="mt-0.5 text-brand-coral" />
                {CONTACT.email}
              </a>
            </li>
          </ul>
          <Link
            to="/contact"
            data-testid="footer-book-call-btn"
            className="mt-7 inline-flex items-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
          >
            Book a call
          </Link>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white">Legal</h3>
          <ul className="mt-5 space-y-3 text-sm">
            {[
              ["Privacy Policy", "/privacy-policy"],
              ["Cookie Policy", "/cookie-policy"],
              ["Complaints", "/complaints"],
              ["Referral Terms", "/referral-terms"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link
                  to={to}
                  data-testid={`footer-legal-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-neutral-400 transition-colors duration-200 hover:text-white"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-16 border-t border-white/10 pt-8 space-y-4 text-xs leading-relaxed text-neutral-500">
        <p data-testid="footer-risk-warning" className="text-sm font-semibold text-neutral-300">{RISK_WARNING}</p>
        <p>{FCA_DISCLOSURE}</p>
        <p>{FEE_DISCLOSURE}</p>
        <p>{COMPANY_DETAILS}</p>
        <p>{UK_REGIME_NOTE}</p>
        <p className="pt-2 text-neutral-600">© {new Date().getFullYear()} BTK Mortgage Services. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default Footer;
